// ============================================================
// PROTAGONISTS - the survival rules a GM hangs on a named character.
//
// Deathwatch combat is bimodal because soak is SUBTRACTIVE and Astartes
// soak is huge: a marine behind TB 8 + AP 10 takes literally nothing from
// an ork slugga, forever, and 26 from a krak missile. Long stretches of
// nothing, then a corpse.
//
// These levers compress that distribution from both ends - the character
// becomes MORE fragile in the small (every hit chips, the wound bar always
// moves) and invulnerable in the large (no exchange kills).
//
// ONLY `deathFloor` GUARANTEES SURVIVAL. Everything else shapes pacing:
// several attackers can still stack past any per-hit cap, and the floor is
// what catches that. Keeping the division clear is what lets the pacing
// knobs be tuned freely without anyone having to re-audit the guarantee.
//
// Pure: no React, no clock, no dice. The clamps live in `damageCalc`, the
// floor at the two sites that can kill; this module owns the SHAPE and
// the single gate that decides whether any of it applies.
// ============================================================

/**
 * Where a death goes instead.
 *
 * `dying` is the one with drama in it: the squad sees a real clock, races
 * it, and loses - and he lives, because the expiry is intercepted too.
 *
 * `dyingFatal` IS THE ONE THAT DOES NOT GUARANTEE ANYTHING, and is here
 * on purpose. The killing blow still does not kill; the clock does. It
 * converts a death decided by one die roll into a death decided by
 * whether the squad can reach him in three rounds - which is a death the
 * table can see coming, argue about, and fail to prevent. Everything
 * else on this tab is a promise; this is a reprieve.
 */
export type DeathFloor = 'none' | 'dying' | 'dyingFatal' | 'unconscious' | 'removed';

/**
 * What a GM has decided about one named character.
 *
 * Every lever is OPTIONAL and absent means "do not clamp". That is what
 * lets `emptyProtagonist()` be genuinely inert, and it is why a half-filled
 * record from an older build cannot change a number nobody set.
 */
export interface Protagonist {
  /**
   * This character's own switch.
   *
   * Separate from the levers so that turning a protagonist off for one
   * fight does not lose the dialling. The table's `protagonistRules`
   * ruling is the OTHER half - both must be true. See `activeProtagonist`.
   */
  enabled: boolean;
  /** A connecting hit removes at least this many wounds. Defeats the soak wall. */
  damageFloor?: number;
  /**
   * The floor helps only the first N hits of one attack.
   *
   * Without it the floor multiplies with rate of fire: floor 2 against ten
   * horde attacks is twenty wounds a round, and the floor is what created
   * that. Hits past the limit resolve normally - which against heavy
   * armour means zero, so the log reads as an ordinary mixed volley and
   * there is nothing to notice.
   */
  chipLimit?: number;
  /** Every incoming damage die counts as at least this. Per die, before bonuses. */
  diceClampMin?: number;
  /** Every incoming damage die counts as at most this. Bites hardest on multi-die weapons. */
  diceClampMax?: number;
  /**
   * No single hit removes more than this FRACTION of maximum wounds.
   *
   * A fraction rather than a wound count because a flat 6 means different
   * things to a 22-wound marine and a 12-wound guardsman, and the GM would
   * be retuning it per character forever.
   */
  damageCeiling?: number;
  /**
   * Ceiling on the soak this character enjoys.
   *
   * Spent against base and natural armour and Toughness FIRST - never
   * against cover. Capping the raw sum would mean power armour alone
   * reaches the ceiling and a rockcrete barricade changes nothing, while
   * the log still prints the cover it granted.
   */
  soakCap?: number;
  /** Where a death goes instead. The guarantee. */
  deathFloor?: DeathFloor;
  /** Crits against this character never exceed this severity. Applied after True Grit. */
  critSeverityCap?: number;
  /**
   * Righteous Fury cannot inflict a Dying or fatal result on this character.
   *
   * Not a severity cap: RF severity is already `1d5`, so it cannot reach the
   * instant-death rows. The real danger is the bare `Dy` rows at severity
   * 5–6 - "dies without treatment", no clock - which RF can land at FULL
   * wounds, out of nowhere.
   */
  rfRestraint?: boolean;
}

/** A protagonist record that clamps nothing. The starting point in the editor. */
export function emptyProtagonist(): Protagonist {
  return { enabled: false, deathFloor: 'none' };
}

export interface ProtagonistPreset {
  key: string;
  label: string;
  /** One line in the GM's language - what the fight will feel like. */
  blurb: string;
  values: Protagonist;
}

/**
 * Three tiers, so a GM is not dialling eight numbers across six marines.
 *
 * Tuned against the reference case the whole feature exists for: a marine
 * of ~22 wounds behind soak 18, taking roughly three connecting hits a
 * round from a mob whose weapons cannot otherwise scratch him.
 *
 * They are starting points, not rulings - every field stays editable, and
 * these numbers deserve a playtest before anyone treats them as settled.
 */
export const PROTAGONIST_PRESETS: ProtagonistPreset[] = [
  {
    key: 'light',
    label: 'Light',
    blurb: 'He can be hurt, and he will not die. Roughly seven rounds of massed small arms.',
    values: {
      enabled: true,
      damageFloor: 1,
      chipLimit: 3,
      damageCeiling: 0.5,
      deathFloor: 'dying',
      critSeverityCap: 8,
      rfRestraint: false,
    },
  },
  {
    key: 'standard',
    label: 'Standard',
    blurb: 'Every fight costs him. Four rounds of massed small arms, and the big guns still frighten.',
    values: {
      enabled: true,
      damageFloor: 2,
      chipLimit: 2,
      diceClampMin: 4,
      diceClampMax: 8,
      damageCeiling: 0.4,
      soakCap: 12,
      deathFloor: 'dying',
      critSeverityCap: 7,
      rfRestraint: true,
    },
  },
  {
    key: 'heavy',
    label: 'Heavy',
    blurb: 'He will be carried out. Two or three rounds, and the squad will have to come for him.',
    values: {
      enabled: true,
      damageFloor: 3,
      chipLimit: 2,
      diceClampMin: 4,
      diceClampMax: 7,
      damageCeiling: 0.3,
      soakCap: 10,
      deathFloor: 'unconscious',
      critSeverityCap: 6,
      rfRestraint: true,
    },
  },
];

/** A preset by key, or undefined - a stored key is untrusted input. */
export function presetByKey(key: string | null | undefined): ProtagonistPreset | undefined {
  return PROTAGONIST_PRESETS.find((p) => p.key === key);
}

/** What happens instead of a death. */
export interface DeathFloorOutcome {
  /** Condition codes to apply in place of `'K'`. */
  conditions: string[];
  /** What the log says, in the GM's language. */
  note: string;
}

/**
 * Why the character was about to die. The floor answers differently.
 *
 * `dyingExpired` NEVER yields another clock. A floor of `'dying'` writes
 * `Dy3`, and a timed `Dy` that runs out is itself a death - so answering
 * an expiry with a fresh `Dy3` would loop forever and the clock would
 * mean nothing. Dropping to unconscious instead is also the better beat:
 * the squad sees a real clock, races it, LOSES - and he lives.
 */
export type DeathCause = 'kill' | 'dyingExpired';

/**
 * What to do instead of killing this character.
 *
 * THE GUARANTEE, for every floor except `dyingFatal` - which deliberately
 * only defers.
 *
 * Returns null when death stands - no protagonist, a floor of `'none'`,
 * or `dyingFatal` at the end of its clock. Two sites ask it, because two
 * sites can kill: the critical `'K'` and a `Dying` clock expiring.
 *
 * BURNING IS NOT ONE OF THEM, though it looks like it should be. It has
 * no death check of its own, so it never kills directly - it kills by
 * driving wounds negative until an ordinary hit arrives as a severity-10
 * critical, and that critical comes through here. Flooring the burn as
 * well was tried and reverted: it pinned a survivor at zero wounds, which
 * made him the one combatant who could never be found below zero.
 *
 * `'removed'` resolves to unconscious like the others, because the app
 * has no notion of removing someone from an encounter; the note says so,
 * and moving the token is the GM's move.
 */
export function resolveDeathFloor(prot: Protagonist | undefined, cause: DeathCause = 'kill'): DeathFloorOutcome | null {
  const floor = prot?.deathFloor;
  if (!floor || floor === 'none') return null;

  if (cause === 'dyingExpired') {
    // THE ONE FLOOR THAT LETS GO. It bought him three rounds and no more;
    // if nobody reached him, the clock is allowed to finish what the blow
    // started. Returning null here is the whole difference between it and
    // `dying`.
    if (floor === 'dyingFatal') return null;
    return { conditions: ['U'], note: '✴ The wound was mortal - and he is still breathing. Unconscious.' };
  }
  switch (floor) {
    case 'dying':
      return { conditions: ['Dy3'], note: '✴ A killing blow, survived. Dying - three rounds to reach him.' };
    case 'dyingFatal':
      return {
        conditions: ['Dy3'],
        note: '✴ A killing blow, survived - for now. Dying: three rounds to reach him, or he is gone.',
      };
    case 'unconscious':
      return { conditions: ['U'], note: '✴ A killing blow, survived. Unconscious.' };
    case 'removed':
      return {
        conditions: ['U'],
        note: '✴ A killing blow, survived. Out of the fight - unconscious at 0 wounds.',
      };
  }
}

/**
 * THE SINGLE GATE. The protagonist rules in force for this defender, if any.
 *
 * Two switches must agree: the table's `protagonistRules` ruling and the
 * character's own `enabled`. The table half is ON by default and exists
 * so a table can switch the whole idea off in one place; the character
 * half is what actually selects who it applies to, and defaults to off.
 *
 * That order matters. The table ruling shipped off, on the argument that
 * sheets travel - but the common mistake is not "somebody handed me plot
 * armour", it is a GM filling in the tab, playing, and watching the
 * character die anyway. Sharing is guarded where it belongs: an export
 * leaves these rules behind unless asked.
 *
 * Deliberately gates CONSUMPTION rather than projection. An earlier design
 * omitted these fields from `Target` when the rule was off, which would
 * have been a silent, table-wide bug: `projectDefender`'s cache validates
 * only sheet/overrides/state/name identity, so flipping the ruling
 * mid-session would have returned the stale Target BY REFERENCE and no
 * memoised consumer would have re-rendered. The field is therefore always
 * projected, and every reader asks this function instead.
 */
export function activeProtagonist(
  target: { protagonist?: Protagonist } | null | undefined,
  rulesEnabled: boolean,
): Protagonist | undefined {
  if (!rulesEnabled) return undefined;
  const p = target?.protagonist;
  if (!p?.enabled) return undefined;
  return p;
}
